import { createTheme } from "@mui/material/styles";

// Design tokens ported from the "Botriq Customer Portal" redesign mockup's
// CSS custom properties (:root light / [data-theme="dark"] dark).
const LIGHT = {
  bg: "#f6f6f7",
  surface: "#ffffff",
  surface2: "#fafafa",
  surface3: "#f2f2f4",
  // Fill for active (editable) input fields — a clearly greyer tone than the
  // read box so edit mode stands out (dark has its own greyer value below).
  editFill: "#e7e7ec",
  border: "#e6e6ea",
  borderStrong: "#d6d6dc",
  text: "#18181b",
  text2: "#5b5b64",
  text3: "#9a9aa2",
  accent: "#3d6ae0",
  accent2: "#2f56c0",
  accentSoft: "rgba(61,106,224,.10)",
  success: "#16a34a",
  successSoft: "rgba(22,163,74,.10)",
  warning: "#d97706",
  warningSoft: "rgba(217,119,6,.11)",
  danger: "#dc2626",
  dangerSoft: "rgba(220,38,38,.10)",
  shadow: "0 1px 2px rgba(0,0,0,.06)",
  shadowLg: "0 12px 40px -8px rgba(0,0,0,.14)",
  headerBg: "rgba(246,246,247,0.82)",
};

const DARK = {
  // Matches the mockup's [data-theme="dark"] CSS vars. The key to the crisp
  // look: bg is meaningfully darker than surface, so cards visibly float, and
  // borders (#222228, borderStrong #2d2d35) read as clean edges against that
  // near-black background rather than blending in.
  bg: "#050506",
  surface: "#111114",
  surface2: "#16161b",
  surface3: "#1d1d23",
  // Two consistent border steps against the near-black surfaces: `border` is the
  // resting edge, `borderStrong` is genuinely MORE visible (lighter) for hover /
  // input outlines / emphasis. (Previously borderStrong was darker than border,
  // so "strong" edges looked weaker in dark and misaligned with the design.)
  border: "#2b2b33",
  borderStrong: "#43434e",
  // Active-input fill — a distinctly greyer tone than the card/read box so an
  // editable field's BACKGROUND (not just its text) clearly signals edit mode.
  editFill: "#31313a",
  text: "#F4F4F5",
  text2: "#9b9ba4",
  text3: "#63636d",
  accent: "#5B8CFF",
  accent2: "#7ba4ff",
  accentSoft: "rgba(91,140,255,.14)",
  success: "#22C55E",
  successSoft: "rgba(34,197,94,.13)",
  warning: "#F59E0B",
  warningSoft: "rgba(245,158,11,.13)",
  danger: "#EF4444",
  dangerSoft: "rgba(239,68,68,.13)",
  shadow: "0 1px 2px rgba(0,0,0,.4)",
  shadowLg: "0 12px 40px -8px rgba(0,0,0,.6)",
  // Translucent header background (matches --bg tint, adapts per mode).
  headerBg: "rgba(9,9,11,0.82)",
};

// ── Live theming via CSS custom properties ──────────────────────────────────
// `tokens` is imported directly (non-hook) by ~20 page/component files as
// `import { tokens } from '../../theme'` and used in CSS contexts (sx / inline
// style). Instead of holding hex VALUES (which forced a full app remount to
// repaint on toggle), each token is a CSS var() REFERENCE whose identity never
// changes. The actual colors live as CSS custom properties on :root and flip
// via `data-theme` on <html>, so a theme switch is an instant browser repaint —
// no React remount, no refetch, no stale colors. Call sites are unchanged
// (`tokens.text` now resolves to `var(--bq-text)`).
const CSS_VAR_PREFIX = "--bq-";
export const tokens = Object.keys(LIGHT).reduce((acc, key) => {
  acc[key] = `var(${CSS_VAR_PREFIX}${key})`;
  return acc;
}, {});

// Serialize a token map to a CSS declaration block.
const toVars = (map) =>
  Object.entries(map)
    .map(([k, v]) => `${CSS_VAR_PREFIX}${k}: ${v};`)
    .join("");

// Inject the palette once: light by default, dark via prefers-color-scheme AND
// via an explicit [data-theme] override (the toggle wins in both directions).
const STYLE_ID = "bq-theme-vars";
const ensureThemeStyle = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent =
    `:root{${toVars(LIGHT)}}` +
    `@media (prefers-color-scheme: dark){:root:not([data-theme="light"]){${toVars(DARK)}}}` +
    `:root[data-theme="light"]{${toVars(LIGHT)}}` +
    `:root[data-theme="dark"]{${toVars(DARK)}}`;
  document.head.appendChild(style);
};
ensureThemeStyle();

// Flip the theme instantly by stamping <html data-theme>. No React work needed.
const setThemeMode = (mode) => {
  ensureThemeStyle();
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", mode === "dark" ? "dark" : "light");
  }
};

export { setThemeMode, LIGHT, DARK };

export const buildMuiTheme = (mode) => {
  // MUI computes contrast/hover shades from these, so feed it the concrete hex
  // palette (not the var() references in `tokens`).
  const p = mode === "dark" ? DARK : LIGHT;
  return createTheme({
    palette: {
      mode,
      background: { default: p.bg, paper: p.surface },
      primary: { main: p.accent, dark: p.accent2, contrastText: "#fff" },
      success: { main: p.success },
      warning: { main: p.warning },
      error: { main: p.danger },
      text: { primary: p.text, secondary: p.text2, disabled: p.text3 },
      divider: p.border,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: "'Inter',system-ui,-apple-system,sans-serif",
      h1: { fontSize: 20, fontWeight: 700, letterSpacing: "-.02em" },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${p.border}`,
            borderRadius: 12,
            boxShadow: "none",
            backgroundColor: p.surface,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 6 },
        },
      },
    },
  });
};

// Default export kept for any existing `import theme from '../../theme'` — a
// static light theme. App.js switches to ThemeModeProvider's live theme.
const theme = buildMuiTheme("light");
export default theme;
